import { useMemo } from "react";

type Tone = "org" | "agent" | "deny" | "ask" | "dlp" | "pr";

interface Node {
  id: string;
  label: string;
  tone: Tone;
  x: number;
  y: number;
  r: number;
}
interface Edge {
  from: string;
  to: string;
}

interface Props {
  agents: string[];
  denies: Array<{ label: string; agent?: string }>;
  asks: Array<{ label: string; agent?: string }>;
  dlp: Array<{ label: string }>;
  pr: Array<{ label: string; score?: number }>;
}

const TONE_COLOR: Record<Tone, string> = {
  org: "var(--accent, #3b82f6)",
  agent: "var(--ink-2, #c8cad0)",
  deny: "var(--red, #f06b62)",
  ask: "var(--amber, #e0a64a)",
  dlp: "var(--purple, #b79cff)",
  pr: "var(--green, #4ec27a)",
};

// Compact, deterministic radial "security memory" graph: a central org node,
// a ring of agents, and leaf nodes for the most recent denies / asks / DLP
// hits / PR-risk briefs linked to their agent (or the org). Layout is
// computed (no physics sim) so it renders identically every time and stays
// cheap. A "View full graph" affordance routes to the Memory page.
export function MemoryMiniGraph({ agents, denies, asks, dlp, pr }: Props) {
  const W = 360;
  const H = 300;
  const cx = W / 2;
  const cy = H / 2;

  const { nodes, edges, empty } = useMemo(() => {
    const ns: Node[] = [];
    const es: Edge[] = [];
    const agentList = agents.slice(0, 6);
    const center: Node = { id: "org", label: "Org", tone: "org", x: cx, y: cy, r: 16 };
    ns.push(center);

    // Ring of agents.
    const agentPos = new Map<string, { x: number; y: number }>();
    const ringR = 86;
    const count = Math.max(1, agentList.length);
    agentList.forEach((a, i) => {
      const ang = (i / count) * Math.PI * 2 - Math.PI / 2;
      const x = cx + Math.cos(ang) * ringR;
      const y = cy + Math.sin(ang) * ringR;
      agentPos.set(a, { x, y });
      ns.push({ id: `agent:${a}`, label: a, tone: "agent", x, y, r: 9 });
      es.push({ from: "org", to: `agent:${a}` });
    });

    // Leaf placement around the agent (or org) the signal belongs to.
    const leafR = 52;
    let leafSeed = 0;
    const addLeaf = (tone: Tone, label: string, agent: string | undefined, idx: number) => {
      const anchor = agent && agentPos.get(agent) ? agentPos.get(agent)! : { x: cx, y: cy };
      const ang = (leafSeed * 47 + idx * 90) * (Math.PI / 180);
      leafSeed++;
      const x = Math.max(14, Math.min(W - 14, anchor.x + Math.cos(ang) * leafR));
      const y = Math.max(14, Math.min(H - 14, anchor.y + Math.sin(ang) * leafR));
      const id = `${tone}:${idx}:${label}`;
      ns.push({ id, label, tone, x, y, r: 6 });
      // agentPos is keyed by the BARE agent name; check that (not the
      // "agent:"-prefixed node id) or every leaf falsely re-roots to org.
      es.push({ from: agent && agentPos.has(agent) ? `agent:${agent}` : "org", to: id });
    };

    denies.slice(0, 4).forEach((d, i) => addLeaf("deny", d.label, d.agent, i));
    asks.slice(0, 3).forEach((d, i) => addLeaf("ask", d.label, d.agent, i));
    dlp.slice(0, 3).forEach((d, i) => addLeaf("dlp", d.label, undefined, i));
    pr.slice(0, 2).forEach((d, i) => addLeaf("pr", d.label, undefined, i));

    return { nodes: ns, edges: es, empty: ns.length <= 1 };
  }, [agents, denies, asks, dlp, pr]);

  return (
    <svg
      className="sr-graph__svg"
      viewBox={`0 0 ${W} ${H}`}
      role="img"
      aria-label="Security memory relationship graph"
    >
      {edges.map((e, i) => {
        const a = nodes.find((n) => n.id === e.from);
        const b = nodes.find((n) => n.id === e.to);
        if (!a || !b) return null;
        return (
          <line
            key={i}
            x1={a.x}
            y1={a.y}
            x2={b.x}
            y2={b.y}
            stroke="var(--line-strong, #34353c)"
            strokeWidth="1"
            strokeOpacity="0.7"
          />
        );
      })}
      {nodes.map((n) => (
        <g key={n.id}>
          <circle
            cx={n.x}
            cy={n.y}
            r={n.r}
            fill={TONE_COLOR[n.tone]}
            fillOpacity={n.tone === "agent" ? 0.9 : n.tone === "org" ? 1 : 0.92}
            stroke="var(--panel, #161618)"
            strokeWidth="2"
          />
          {n.tone === "org" || n.tone === "agent" ? (
            <text
              x={n.x}
              y={n.y + n.r + 11}
              textAnchor="middle"
              fontSize="9.5"
              fill="var(--muted, #8a8e96)"
              fontFamily="var(--font-sans)"
            >
              {n.label.length > 14 ? n.label.slice(0, 13) + "…" : n.label}
            </text>
          ) : null}
        </g>
      ))}
      {empty ? (
        <text
          x={W / 2}
          y={H / 2}
          textAnchor="middle"
          fill="var(--muted, #8a8e96)"
          fontSize="12"
          fontFamily="var(--font-sans)"
        >
          No linked security memory yet
        </text>
      ) : null}
    </svg>
  );
}
